<template>
  <section class="rbs-step" aria-labelledby="rbs-heading" data-testid="readiness-badges-step">
    <div class="rbs-heading">
      <h2 id="rbs-heading" class="rbs-title" data-testid="rbs-title">{{ title }}</h2>
    </div>

    <div class="rbs-content">
      <p v-if="introText" class="rbs-intro" data-testid="rbs-intro">{{ introText }}</p>

      <ul v-if="levels.length" class="rbs-levels" data-testid="rbs-levels">
        <li v-for="level in levels" :key="level.name" class="rbs-level" data-testid="rbs-level">
          <ReadinessBadge
            class="rbs-badge"
            :level="level.name"
            view="ReadOnly"
            hide-chatter-icon
            data-testid="rbs-badge"
          />
          <div class="rbs-level-copy">
            <p class="rbs-level-heading">
              <span class="rbs-level-name" data-testid="rbs-level-name">{{ level.name }}</span>
              <span v-if="level.tag" class="rbs-level-tag" data-testid="rbs-level-tag"
                >· {{ level.tag }}</span
              >
            </p>
            <p
              v-if="level.description"
              class="rbs-level-description"
              data-testid="rbs-level-description"
            >
              {{ level.description }}
            </p>
          </div>
        </li>
      </ul>

      <p v-if="noviceText" class="rbs-novice" data-testid="rbs-novice">{{ noviceText }}</p>
    </div>

    <div class="rbs-spacer" aria-hidden="true"></div>

    <div class="rbs-actions">
      <StandardButton variant="primary" data-testid="rbs-continue" @click="handleContinue">
        {{ buttonLabel }}
      </StandardButton>
    </div>
  </section>
</template>

<script setup>
import ReadinessBadge from "@components/ReadinessBadge/ReadinessBadge.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

/**
 * ER-12245 — ReadinessBadgesStep (MDP-621).
 *
 * "Earn your readiness badges" — the final page of the Employee and
 * Admin + Employee wizards. Lists the readiness levels (shared ReadinessBadge,
 * name, tag, description) and the Novice note, then the flow's final button ("Go to your
 * responsibilities" for Employee, "Go to your organization" for
 * Admin + Employee). Presentational only: copy, levels and the button label
 * arrive as props, and the component emits `continue`.
 */
defineProps({
  /** Step title. */
  title: {
    type: String,
    default: "Earn your readiness badges",
  },
  /** Intro paragraph. */
  introText: {
    type: String,
    default:
      "Every responsibility assigned to you has its own badge. The badge level shows how ready you are for that responsibility.",
  },
  /** Readiness levels: `{ name, tag, description }`; `name` is the ReadinessBadge level. */
  levels: {
    type: Array,
    default: () => [],
  },
  /** The Novice note shown beneath the levels. */
  noviceText: {
    type: String,
    default: "Until you earn a badge for a responsibility, you are a Novice for it.",
  },
  /** Final action label. */
  buttonLabel: {
    type: String,
    default: "Go to your responsibilities",
  },
});

const emit = defineEmits(["continue"]);

function handleContinue() {
  emit("continue");
}
</script>

<style scoped>
.rbs-step {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  height: 100%;
  padding: 0;
  background: #ffffff;
  color: #101828;
  font-family: Inter, sans-serif;
}

.rbs-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rbs-title {
  margin: 0;
  color: #101828;
  font-size: 36px;
  font-weight: 600;
  line-height: 44px;
}

.rbs-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rbs-intro {
  margin: 0;
  color: #475467;
  font-size: 18px;
  line-height: 28px;
}

.rbs-levels {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.rbs-level {
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

.rbs-badge {
  flex: 0 0 auto;
}

.rbs-level-copy {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.rbs-level-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin: 0;
}

.rbs-level-name {
  color: #101828;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.rbs-level-tag {
  color: #475467;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.rbs-level-description {
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.rbs-novice {
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.rbs-spacer {
  flex: 1 1 auto;
}

.rbs-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
